import React, { useEffect, useState } from "react";
import { Container, Table, Alert, Button, Spinner, Modal, Form } from "react-bootstrap";
import API from "../../services/api";
import "../EmployeeAccounts.css";

export const orDash = (v) => (v === 0 ? "0" : v == null || String(v).trim() === "" ? "—" : v);
export const fmtDate = (d) => (d ? new Date(d).toLocaleString("en-IN") : "—");

export const normalizeRows = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const DepartmentListPage = ({
  title,
  subtitle,
  endpoint,
  columns,
  createFields = [],
  createPerms = [],
  approvePerms = [],
  allowEdit = false,
  statusActions = [
    { label: "Approve", value: "approved", variant: "success" },
    { label: "Reject", value: "rejected", variant: "outline-danger" },
  ],
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [perms, setPerms] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({});
  const [editingRow, setEditingRow] = useState(null);
  const [fieldOptions, setFieldOptions] = useState({});
  const optionsConfigKey = createFields.map((f) => `${f.optionsEndpoint || ''}:${f.optionsKey || ''}`).join('|');

  const fetchRows = async () => {
    try {
      setLoading(true);
      setErr("");
      const res = await API.get(endpoint);
      setRows(normalizeRows(res.data));
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "Failed to load data");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, [endpoint]);

  useEffect(() => {
    const fetchPerms = async () => {
      try {
        const res = await API.get("/auth/me");
        const p = res.data?.employeeRole?.permissions || [];
        setPerms(Array.isArray(p) ? p : []);
      } catch {
        setPerms([]);
      }
    };
    fetchPerms();
  }, []);

  useEffect(() => {
    const endpoints = [...new Set(createFields.map((f) => f.optionsEndpoint).filter(Boolean))];
    if (!endpoints.length) return;
    Promise.all(endpoints.map((url) => API.get(url).then((r) => [url, r.data]).catch(() => [url, {}])))
      .then((entries) => setFieldOptions(Object.fromEntries(entries)));
  }, [optionsConfigKey]);

  const canCreate = createPerms.length === 0 || createPerms.some((p) => perms.includes(p));
  const canEdit = allowEdit && canCreate;
  const canApprove = approvePerms.length > 0 && approvePerms.some((p) => perms.includes(p));

  const openCreate = () => {
    const init = {};
    createFields.forEach((f) => (init[f.key] = ""));
    setForm(init);
    setEditingRow(null);
    setShowCreate(true);
  };

  const openEdit = (row) => {
    const init = {};
    createFields.forEach((f) => (init[f.key] = row?.[f.key] ?? ""));
    setForm(init);
    setEditingRow(row);
    setShowCreate(true);
  };

  const closeForm = () => {
    setShowCreate(false);
    setEditingRow(null);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    try {
      if (editingRow?._id) await API.put(`${endpoint}/${editingRow._id}`, form);
      else await API.post(endpoint, form);
      closeForm();
      fetchRows();
    } catch (e1) {
      setErr(e1?.response?.data?.message || (editingRow ? "Update failed" : "Create failed"));
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await API.patch(`${endpoint}/${id}/status`, { status });
      fetchRows();
    } catch (e1) {
      setErr(e1?.response?.data?.message || "Update failed");
    }
  };

  const tableColumns = (() => {
    const cols = [...columns];
    if ((canEdit || canApprove) && (statusActions.length || canEdit)) {
      cols.push({
        key: "_actions",
        label: "Action",
        align: "right",
        render: (r) => (
          <div className="accounts-row-actions">
            {canEdit ? (
              <Button size="sm" variant="outline-dark" onClick={() => openEdit(r)}>
                Edit
              </Button>
            ) : null}
            {canApprove
              ? statusActions.map((action) => (
                  <Button
                    key={action.value}
                    size="sm"
                    variant={action.variant || "outline-secondary"}
                    onClick={() => updateStatus(r._id, action.value)}
                  >
                    {action.label}
                  </Button>
                ))
              : null}
          </div>
        ),
      });
    }
    return cols;
  })();

  return (
    <Container className="mt-4 accounts-page">
      <div className="accounts-header">
        <div>
          <div className="accounts-title-row">
            <h4 className="mb-0">{title}</h4>
            <span className="accounts-count">{rows.length} total</span>
          </div>
          <p className="accounts-subtitle">{subtitle}</p>
        </div>
        <div className="accounts-actions">
          <Button variant="outline-secondary" size="sm" onClick={fetchRows}>
            Refresh
          </Button>
          {canCreate && createFields.length ? (
            <Button variant="primary" size="sm" onClick={openCreate}>
              Add New
            </Button>
          ) : null}
        </div>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <div className="accounts-table-card">
          <Table hover responsive className="accounts-table">
            <thead>
              <tr>
                {tableColumns.map((c) => (
                  <th key={c.key} className={c.align === "right" ? "text-end" : ""}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={tableColumns.length} className="text-center">
                    No records found
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => (
                  <tr key={r._id || r.id || i}>
                    {tableColumns.map((c) => (
                      <td key={c.key} className={c.align === "right" ? "text-end" : ""}>
                        {c.render ? c.render(r) : orDash(r[c.key])}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      )}

      <Modal show={showCreate} onHide={closeForm} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingRow ? "Edit" : "Add"} {title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={submitForm}>
            {createFields.map((f) => (
              <Form.Group className="mb-3" key={f.key}>
                <Form.Label>{f.label}</Form.Label>
                {f.type === "select" ? (
                  <Form.Select value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} required={!!f.required}>
                    <option value="">Select {f.label}</option>
                    {(f.options || fieldOptions[f.optionsEndpoint]?.[f.optionsKey] || []).map((option) => (
                      <option key={option.value ?? option} value={option.value ?? option}>{option.label ?? option}</option>
                    ))}
                  </Form.Select>
                ) : (
                  <Form.Control type={f.type || "text"} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} required={!!f.required} />
                )}
              </Form.Group>
            ))}
            <div className="d-flex justify-content-end gap-2">
              <Button variant="outline-secondary" onClick={closeForm}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                {editingRow ? "Update" : "Save"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default DepartmentListPage;
